import { paths } from '@/config/paths';
import heroBoyCelebrating from '@/features/hero/assets/avatar-celebrating.svg';
import heroBoySmiling from '@/features/hero/assets/avatar-smiling.svg';
import heroBoyUpset from '@/features/hero/assets/avatar-upset.svg';
import type { HeroStateConfig } from '@/features/hero/config/hero-states-v2';
import {
  includeName,
  isOnTrial,
  makeCta,
  makeStats,
  makeSubtitle,
  SESSION_LIVE_THRESHOLD,
} from '@/features/hero/config/hero-states-v2/hero-states-v2.online-helpers';
import { PROMO_HERO_STATES } from '@/features/hero/config/hero-states-v2/hero-states-v2.promos';
import { openPaymentUrl } from '@/features/payments';
import { getNumberToOrdinalName } from '@/lib/utils';

export const DEFAULT_HERO_STATE_ONLINE: HeroStateConfig = {
  id: 'DEFAULT',
  name: 'Default',
  isEnabled: () => true,
  getTitle: () => 'Hi, Welcome to <highlight>Bhanzu Dashboard!</highlight>',
  getSubtitle: () => 'Your Bhanzu journey starts here',
  getCharacterImage: () => heroBoyCelebrating,
};

// Priority order matters — the parser picks the FIRST enabled state.
//   1-2. Payment states take absolute precedence - payment issues must be
//      resolved before anything else (including live sessions).
//   3. FIRST_SESSION_LIVE — the most time-sensitive moment after payments.
//   4+. Other states in order of importance.
// All base states share the 'primary' group, so exactly one of them resolves
// per deck (first-match, as before the carousel). Stackable promo states
// (hero-states-v2.promos.ts) take NO group — each enabled one adds a slide
// after the resolved primary card. PAYMENT_PENDING and FIRST_SESSION_LIVE
// are `exclusive`: when one of them wins, nothing stacks on it.
const HERO_STATES_V2_ONLINE: HeroStateConfig[] = [
  {
    id: 'PAYMENT_PENDING',
    name: 'Payment Pending',

    // Demands full attention — never one slide among many.
    exclusive: true,
    group: 'primary',

    isEnabled: heroData => heroData.isPaymentPending,

    hasError: heroData => heroData.errorStates.profile,

    // Title defers on batch assignment: with a batch the sessions are scheduled
    // and *will* lock if payment isn't completed; without one there's nothing
    // unlocked yet, so they're already locked.
    getTitle: heroData =>
      heroData.rawData.selectedCourse?.batch_id
        ? 'Sessions Will Be <highlight>Locked</highlight>'
        : 'Sessions Are <highlight>Locked</highlight>',

    getSubtitle: heroData => {
      return heroData.rawData.selectedCourse?.batch_id
        ? 'Your sessions will be locked until payment is completed.'
        : 'Your sessions are locked. Complete payment to unlock.';
    },

    getCta: (heroData, callbacks) => {
      const url = heroData.paymentUrl;
      return {
        variant: 'raised-jumbo',
        label: 'Complete Payment',
        onClick: () =>
          openPaymentUrl(url, {
            source: 'heroCard',
            trackEvent: callbacks?.trackEvent,
          }),
      };
    },

    getCharacterImage: () => heroBoyUpset,
  },
  {
    id: 'FIRST_SESSION_LIVE',
    name: 'First Session Live',

    // Time-sensitive — renders alone so the join prompt can't be scrolled away.
    exclusive: true,
    group: 'primary',

    // The selected course's very first session (number === 1) going live. A
    // late joiner whose first session already happened has number > 1, so they
    // won't match — exactly what we want.
    isEnabled: heroData => {
      const { upcomingSession, selectedCourse } = heroData.rawData;
      return (
        upcomingSession?.variant_id !== undefined &&
        upcomingSession.variant_id === selectedCourse?.variant_id &&
        upcomingSession.number === 1 &&
        (upcomingSession.remaining_seconds ?? Infinity) <=
          SESSION_LIVE_THRESHOLD
      );
    },

    hasError: heroData => heroData.errorStates.upcomingSession,

    // "Trial" copy when the selected course is on trial.
    getTitle: heroData =>
      isOnTrial(heroData)
        ? 'Your First Trial Session Is <highlight>LIVE</highlight>'
        : 'Your First Session Is <highlight>LIVE</highlight>',

    getStats: () => 'Join from the session card',

    getCharacterImage: () => heroBoySmiling,
  },

  {
    id: 'TRIAL_SESSION_ATTENDED',
    name: 'Trial Session Attended',

    group: 'primary',

    isEnabled: heroData =>
      isOnTrial(heroData) &&
      heroData.lastSession?.attendance?.status === 'ATTENDED',

    hasError: heroData => {
      return (
        heroData.errorStates.modules || heroData.errorStates.pendingAssessments
      );
    },

    getTitle: heroData => {
      return `Great Job${includeName(heroData)} Attending The Session!`;
    },

    getSubtitle: () =>
      `Let's lock in your learning — your next assessment is ready`,

    getStats: () => `Your friends have been practicing — now it's your turn!`,

    // makeCta with trial-funnel copy; its MISSED branch can't fire here
    // because this state requires an attended last session.
    getCta: (heroData, callbacks) => ({
      ...makeCta(heroData, callbacks),
      variant: 'raised-jumbo',
      label: 'Start Assessment',
    }),

    getCharacterImage: () => heroBoyCelebrating,
  },

  {
    id: 'TRIAL_SESSION_MISSED',
    name: 'Trial Session Missed',

    group: 'primary',

    isEnabled: heroData =>
      isOnTrial(heroData) &&
      heroData.lastSession?.attendance?.status === 'MISSED',

    hasError: heroData => {
      return (
        heroData.errorStates.modules || heroData.errorStates.pendingAssessments
      );
    },

    getTitle: heroData => {
      return `Uh-Oh${includeName(heroData)}! You <highlight>Missed</highlight> Your Trial Session`;
    },

    getSubtitle: heroData => {
      const sessionName = heroData.lastSession?.name;
      if (sessionName) {
        return `You missed your ${sessionName} session`;
      }
      return 'You missed your trial session';
    },

    getDivider: () => true,

    getOverButton: () => `But don't worry — here's what you missed`,

    getCta: (heroData, callbacks) => {
      return {
        variant: heroData.shouldShowSecondaryButton
          ? 'outline'
          : 'raised-jumbo',
        label: 'Watch Recording',
        onClick: () => {
          callbacks?.openRecordingDrawer?.();
        },
      };
    },

    getCharacterImage: () => heroBoyUpset,
  },

  {
    id: 'SCHOOL_CURRICULUM',
    name: 'School Curriculum',

    group: 'primary',

    isEnabled: heroData =>
      heroData.rawData.selectedCourse?.course_name
        ?.toLowerCase()
        .trim()
        .endsWith('academic maths') ?? false,

    hasError: heroData => heroData.errorStates.profile,

    getTitle: () => {
      return 'Watch Sessions of School Curriculum';
    },

    getDivider: () => true,

    getStats: () => "Your friends have been watching - now it's your turn!",

    getCta: (_, callbacks) => {
      return {
        variant: 'raised-jumbo',
        label: 'Watch Recordings',
        onClick: async () => {
          await callbacks?.navigate?.(paths.app.modules.getHref());
        },
      };
    },

    getCharacterImage: () => heroBoySmiling,
  },

  {
    id: 'STARTED_NEW_MODULE',
    name: 'Started New Module',

    group: 'primary',

    isEnabled: heroData => {
      return heroData.hasNewModuleStarted;
    },

    hasError: heroData => {
      return (
        heroData.errorStates.modules || heroData.errorStates.pendingAssessments
      );
    },

    getTitle: heroData => {
      const moduleNumber = heroData.currentOngoingModule?.module.number;
      const ordinalModuleNumber = getNumberToOrdinalName(Number(moduleNumber));
      if (ordinalModuleNumber) {
        return `Great Going${includeName(heroData)}! Your <highlight>${ordinalModuleNumber} Module</highlight> Has Kicked Off`;
      }
      return `Great Going${includeName(heroData)}! Your <highlight>New Module</highlight> Has Kicked Off`;
    },

    getSubtitle: heroData => makeSubtitle(heroData),

    getStats: makeStats,

    getCta: (heroData, callbacks) => makeCta(heroData, callbacks),

    getCharacterImage: () => heroBoyCelebrating,
  },

  {
    id: 'ATTENDED_PREVIOUS_SESSION',
    name: 'Attended Previous Session',

    group: 'primary',

    isEnabled: heroData => {
      return heroData.lastSession?.attendance?.status === 'ATTENDED';
    },

    hasError: heroData => {
      return (
        heroData.errorStates.modules || heroData.errorStates.pendingAssessments
      );
    },

    getTitle: heroData => {
      return `Great Job${includeName(heroData)}, Attending Your Session!`;
    },

    getSubtitle: heroData => makeSubtitle(heroData),

    getStats: makeStats,

    getCta: (heroData, callbacks) => makeCta(heroData, callbacks),

    getCharacterImage: () => heroBoySmiling,
  },

  {
    id: 'MISSED_1_SESSION',
    name: 'Missed 1 Session',

    group: 'primary',

    isEnabled: heroData => {
      return heroData.lastSession?.attendance?.status === 'MISSED';
    },

    hasError: heroData => {
      return (
        heroData.errorStates.modules || heroData.errorStates.pendingAssessments
      );
    },

    getTitle: heroData => {
      return `Uh-Oh${includeName(heroData)}! You <highlight>Missed</highlight> A Session`;
    },

    getSubtitle: () => {
      return `Stay updated by watching your missed session`;
    },

    getCta: (heroData, callbacks) => makeCta(heroData, callbacks),

    getCharacterImage: () => heroBoyUpset,
  },

  {
    ...DEFAULT_HERO_STATE_ONLINE,
    // In the primary group (not `fallback`): it's the group's catch-all, so
    // the welcome card still leads the deck when promos are enabled.
    group: 'primary',
    getTitle: heroData =>
      `Hi${includeName(heroData)}, Welcome to <highlight>Bhanzu Dashboard!</highlight>`,
  },

  // Stackable campaign slides — last, so the one resolved primary card
  // (including the DEFAULT welcome catch-all) is always the first slide.
  ...PROMO_HERO_STATES,
];

export default HERO_STATES_V2_ONLINE;
